<template lang="pug">
.page.blog
  error-layout(
    v-if="$fetchState.error"
    :error="$fetchState.error")

  template(v-else)
    .page__section(v-if="!category")
      .pd-container.framed
        .featured-grid
          template(v-if="$fetchState.pending")
            blog-post-card(
              v-for="k in 4"
              :key="k"
              loading)

          template(v-else)
            blog-post-card(
              v-for="i, k in featuredPosts"
              :key="k"
              :entry="i")

    .page__section.latest(v-if="posts.length")
      .pd-container.framed
        h2.pd-h2(v-if="category") {{ category }}
        h2.pd-h2(v-else) Latest Posts
        .grid
          blog-post-card(
            v-for="i, k in posts"
            :key="k"
            :entry="i")

        .load-more.tc.mt-3(v-if="canFetchMore")
          el-button(
            :loading="fetching"
            :disabled="fetching"
            @click="fetchPosts") Load more

      //- debug(:data="posts" static)
</template>

<script>
import ErrorLayout from '~/layouts/error'
import BlogPostCard from '~/components/blog/BlogPostCard'

const POSTS_LIMIT = 6
const FEATURED_POSTS_LIMIT = 4

export default {
  components: {
    ErrorLayout,
    BlogPostCard
  },

  layout: 'landing',

  scrollToTop: true,

  data () {
    return {
      posts: [],
      featuredPosts: [],
      total: null,
      fetching: false
    }
  },

  fetch () {
    this.posts = []
    this.featuredPosts = []
    this.total = null
    const tasks = [
      this.fetchPosts()
    ]
    if (!this.category)
      tasks
        .push(
          this.$contentful.client
            .getEntries({
              content_type: 'blogPost',
              order: '-fields.featured,-sys.createdAt',
              limit: FEATURED_POSTS_LIMIT
            })
            .then(res => {
              this.featuredPosts = res.items
            })
        )
    return Promise
      .all(tasks)
      .catch(e => {
        const error = { statusCode: 503 }
        if (process.server) {
          this.$nuxt.context.res.statusCode = error.statusCode
          this.$fetchState.error = error
        } else {
          this.$nuxt.error(error)
        }
      })
  },

  head () {
    return this.$seo.head({
      title: 'Blog'
    })
  },

  computed: {
    canFetchMore () {
      if (this.total === null) return true
      return this.posts.length < this.total
    },

    category () {
      return this.$route.query.category
    }
  },

  watch: {
    category: '$fetch'
  },

  methods: {
    fetchPosts () {
      if (!this.canFetchMore) return
      this.fetching = true
      const options = {
        content_type: 'blogPost',
        order: '-sys.createdAt',
        limit: POSTS_LIMIT,
        skip: this.posts.length
      }
      if (this.category)
        options['fields.category'] = this.category
      return this.$contentful.client
        .getEntries(options)
        .then(res => {
          this.posts = [
            ...this.posts,
            ...res.items
          ]
          this.total = res.total
        })
        .finally(() => {
          this.fetching = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.blog {
  .page__section {
    padding: 0;
    margin: 2rem 0;
    @include media(sm) {
      margin: 3rem 0;
    }
    @include media(md) {
      margin: 4rem 0;
    }
  }
  .grid {
    display: grid;
    grid-gap: 1.5rem;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    @include media(md) {
      grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
      grid-gap: 3rem 2rem;
    }
    > * {
      min-width: 0;
    }
  }
  .featured-grid {
    display: grid;
    gap: 1rem;
    @include media(md) {
      grid-template-columns: 1fr 1fr;
      grid-gap: 1rem 2rem;
      grid-template-areas:
        'slot-1 slot-2'
        'slot-1 slot-3'
        'slot-1 slot-4';
    }
    > * {
      min-width: 0;
      @include media(md) {
        @for $i from 1 through 4 {
          &:nth-child(#{$i}) {
            grid-area: slot-#{$i};
          }
        }
      }
      &:first-child {
        ::v-deep {
          .blog-post {
            &__title {
              font-size: 2rem;
            }
          }
        }
      }
      &:not(:first-child) {
        display: flex;
        gap: 1rem;
        @include media(md) {
          gap: 1.5rem;
        }
        > * {
          min-width: 0;
        }
        ::v-deep {
          .blog-post {
            &__thumbnail {
              width: 64px;
              height: 64px;
              padding-bottom: 0;
              @include media(md) {
                width: 128px;
                height: 128px;
              }
            }
            &__info {
              flex: 1;
            }
            &__metas {
              margin-top: 0;
              @include media(sm-down) {
                margin-bottom: 0;
              }
            }
            &__title {
              @include media(sm-down) {
                font-size: 1.2rem;
                margin: .5em 0;
              }
            }
            &__summary {
              display: none;
            }
            &__more {
              @include media(sm-down) {
                display: none;
              }
            }
          }
        }
      }
    }
  }
}
</style>
